/*
 * "Fly to cart" - behaviour only.
 *
 * Native port of the Variant experience exp-EXP9C244C0B. There is nothing to
 * render here: it is an animation plus one rule about when the cart drawer is
 * allowed to open.
 *
 * The original took the add-to-cart over completely - a capture-phase submit
 * listener with stopImmediatePropagation, its own /cart/add.js call, its own
 * section re-rendering, its own cart-count updates, its own quick-buy-modal
 * closing, and a replacement for window.fetch to catch adds that did not go
 * through a form. The theme already does every one of those things and
 * announces them, so all that is left is to watch and to animate.
 */
(function () {
  'use strict';

  var ANIMATION_MS = 1050;

  /*
   * How long the drawer is held shut. The hold starts when the shopper commits
   * (the submit) and is cut back to the tail once the add has landed, because
   * some openers wait a frame or two after the cart changes. The long window is
   * only ever reached when the add never lands at all - a slow network, or an
   * error - and even then a tap on the cart releases it immediately.
   */
  var DRAWER_HOLD_SETTLE_MS = 600;
  var DRAWER_HOLD_MAX_MS = 8000;

  /*
   * The experiment carried a rule that held the drawer shut until the cart was
   * worth more than 300 ILS. It never actually ran - see the cart:change
   * listener at the bottom - so no shopper has seen it, and it is not
   * reinstated here: the drawer stays shut for the card adds this animates, and
   * opens everywhere else exactly as it does today.
   */

  var MOBILE_QUERY = '(max-width: 767px)';

  /*
   * Every kind of card that carries a quick add: the theme's own product card
   * (collection grid, featured collections, shop the look), the quick buy modal
   * those open for a product with options, and the two homepage sections that
   * bring cards of their own.
   */
  var CARD_SELECTOR =
    'product-card, quick-buy-modal, .tabbed-categories__card, .motion-carousel__card';

  function isMobile() {
    return !window.matchMedia || window.matchMedia(MOBILE_QUERY).matches;
  }

  function pageType() {
    var variables = window.themeVariables;
    return (variables && variables.settings && variables.settings.pageType) || '';
  }

  /*
   * Where an add is one card among many the shopper is still browsing, rather
   * than the product they came for. The script is only loaded on these pages;
   * the check is here because the page can be either one of them.
   */
  function isListingPage() {
    var type = pageType();
    return type === 'collection' || type === 'index';
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function cartIcon() {
    return (
      document.querySelector('.icon-cart') ||
      document.querySelector('cart-dot.cart-count') ||
      document.querySelector('.cart-count')
    );
  }

  function productImage(scope) {
    return (
      scope.querySelector('.product-card__image--primary') ||
      scope.querySelector('.product-card__figure img') ||
      scope.querySelector('.tabbed-categories__image') ||
      scope.querySelector('.motion-carousel__image') ||
      scope.querySelector('img')
    );
  }

  /**
   * The URL the browser actually settled on, so the thumbnail that flies is
   * one already in cache and never renders as a blank box.
   */
  function imageUrl(image) {
    var url = image.currentSrc || image.src || '';

    if (!url) {
      var srcset = image.getAttribute('srcset');
      if (srcset) {
        var candidates = srcset.split(',').map(function (entry) {
          return entry.trim().split(/\s+/);
        });
        var midSized = candidates.filter(function (parts) {
          return parts[1] === '300w' || parts[1] === '400w' || parts[1] === '500w';
        })[0];
        url = (midSized || candidates[0] || [])[0] || '';
      }
    }

    return url.indexOf('//') === 0 ? 'https:' + url : url;
  }

  /**
   * Tosses a copy of the product thumbnail along an arc into the cart icon.
   * The path is a quadratic curve sampled into keyframes, because the Web
   * Animations API interpolates transforms in straight lines between them.
   */
  function flyToCart(image) {
    if (!image || prefersReducedMotion()) return;

    var target = cartIcon();
    if (!target) return;

    var from = image.getBoundingClientRect();
    if (!from.width || !from.height) return;
    var to = target.getBoundingClientRect();

    var width = 68;
    var height = 90;
    var startX = from.left + from.width / 2 - width / 2;
    var startY = from.top + from.height / 2 - height / 2;
    var deltaX = to.left + to.width / 2 - width / 2 - startX;
    var deltaY = to.top + to.height / 2 - height / 2 - startY;

    var flier = document.createElement('div');
    flier.setAttribute('aria-hidden', 'true');
    flier.className = 'fly-to-cart';
    flier.style.left = startX + 'px';
    flier.style.top = startY + 'px';

    var url = imageUrl(image);
    if (url) {
      var thumbnail = document.createElement('img');
      thumbnail.src = url;
      thumbnail.alt = '';
      flier.appendChild(thumbnail);
    } else {
      flier.classList.add('fly-to-cart--blank');
    }

    document.body.appendChild(flier);

    // The arc peaks above both ends, which is what makes it read as a throw
    // rather than a slide.
    var arcHeight = Math.max(80, Math.abs(deltaY) * 0.5);
    var controlX = deltaX * 0.35;
    var controlY = Math.min(0, deltaY) - arcHeight;

    var steps = 28;
    var keyframes = [];

    for (var step = 0; step <= steps; step++) {
      var t = step / steps;
      var eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      var inverse = 1 - eased;

      var x = 2 * inverse * eased * controlX + eased * eased * deltaX;
      var y = 2 * inverse * eased * controlY + eased * eased * deltaY;
      var scale = 1 - 0.92 * eased;
      var rotation = Math.sin(t * Math.PI) * -14;
      var opacity = t < 0.75 ? 1 : 1 - (t - 0.75) / 0.25;

      keyframes.push({
        offset: t,
        transform:
          'translate(' + x + 'px, ' + y + 'px) scale(' + scale + ') rotate(' + rotation + 'deg)',
        opacity: Math.max(0, opacity).toFixed(3)
      });
    }

    var animation = flier.animate(keyframes, {
      duration: ANIMATION_MS,
      // The easing is already baked into where the keyframes sit.
      easing: 'linear',
      fill: 'forwards'
    });

    // Land the throw with a nudge on the cart icon.
    setTimeout(function () {
      var landing = cartIcon();
      if (!landing) return;
      landing.classList.add('fly-to-cart-landing');
      setTimeout(function () {
        landing.classList.remove('fly-to-cart-landing');
      }, 600);
    }, ANIMATION_MS * 0.88);

    function cleanUp() {
      if (flier.parentNode) flier.parentNode.removeChild(flier);
    }

    animation.onfinish = cleanUp;
    // onfinish does not fire while the tab is hidden.
    setTimeout(cleanUp, ANIMATION_MS + 400);
  }

  // -------------------------------------------------------------------------
  // Holding the cart drawer shut
  //
  // When a product is added from a card, the flying thumbnail is the whole
  // confirmation: the shopper stays in the grid or the carousel they were
  // browsing instead of being dropped into the cart. Adds from anywhere else -
  // a product page, an app block, the search overlay - open the drawer as
  // before.
  //
  // There are two ways the drawer gets opened, and the hold has to cover both.
  // The theme calls show() from its own cart:change listener. Several of the
  // custom scripts on the site instead set the "open" attribute straight onto
  // the element, usually inside a requestAnimationFrame, and apps can do the
  // same. (The previous version stubbed show() on the element for a single
  // tick, so anything that opened the drawer a frame later, or by attribute,
  // or after the drawer element had been re-rendered, opened it anyway.)
  //
  // Both are blocked on the prototype rather than on the element, so the hold
  // survives the drawer section being re-rendered. Blocking the attribute is
  // what keeps the drawer from opening at all: the theme opens on the "open"
  // attribute reaction, which runs synchronously and locks the page, so a hold
  // that let the attribute through and took it off afterwards would show the
  // drawer sliding in and back out.
  // -------------------------------------------------------------------------

  var holding = false;
  var holdTimer = null;

  if (window.customElements) {
    customElements.whenDefined('cart-drawer').then(function () {
      var constructor = customElements.get('cart-drawer');
      var prototype = constructor && constructor.prototype;
      if (!prototype) return;

      var show = prototype.show;
      var setAttribute = prototype.setAttribute;
      var toggleAttribute = prototype.toggleAttribute;

      prototype.show = function () {
        if (holding) return Promise.resolve();
        return show.apply(this, arguments);
      };

      prototype.setAttribute = function (name) {
        if (holding && name === 'open') return;
        return setAttribute.apply(this, arguments);
      };

      prototype.toggleAttribute = function (name, force) {
        if (holding && name === 'open' && force !== false) return this.hasAttribute(name);
        return toggleAttribute.apply(this, arguments);
      };
    });
  }

  function holdDrawerShut(duration) {
    var drawer = document.querySelector('cart-drawer');

    // Already open - the shopper put it there, so leave it alone.
    if (drawer && drawer.hasAttribute('open')) return;

    holding = true;

    clearTimeout(holdTimer);
    holdTimer = setTimeout(releaseDrawer, duration);
  }

  function releaseDrawer() {
    clearTimeout(holdTimer);
    holdTimer = null;
    holding = false;
  }

  /*
   * The hold is about the drawer opening by itself. A shopper who reaches for
   * the cart still gets it: this listens in the capture phase, so the hold is
   * gone before the theme's own click handler asks the drawer to show.
   */
  function releaseOnCartIntent(event) {
    if (!holding) return;

    var target = event.target;
    if (!target || !target.closest) return;
    if (target.closest('[aria-controls="cart-drawer"], cart-drawer')) releaseDrawer();
  }

  document.addEventListener('pointerdown', releaseOnCartIntent, true);
  document.addEventListener('click', releaseOnCartIntent, true);

  // The add failed, so there is nothing to hold the drawer shut for.
  document.addEventListener('cart:error', releaseDrawer, true);

  // -------------------------------------------------------------------------
  // Triggers
  // -------------------------------------------------------------------------

  /*
   * Submitting is the moment the shopper committed, and it happens before the
   * request goes out - so the thumbnail leaves immediately rather than after a
   * round trip. The theme's own handler is not disturbed: this listens on the
   * way back up and prevents nothing.
   */
  document.addEventListener('submit', function (event) {
    if (!isMobile()) return;

    var form = event.target;
    if (!form.matches || !form.matches('.shopify-product-form')) return;

    // A quick-buy modal shows the product's own gallery; a card shows its
    // thumbnail. Either way the picture the shopper is looking at is the one
    // that should fly. The modal is the closer of the two when it is open,
    // which is what closest() returns.
    var card = form.closest(CARD_SELECTOR);
    if (!card) return;

    flyToCart(productImage(card));

    // The shopper is staying where they are, so start holding the drawer shut
    // now rather than when the add lands - whatever opens it, the hold is
    // already in place by then.
    if (isListingPage()) holdDrawerShut(DRAWER_HOLD_MAX_MS);
  });

  /*
   * The homepage motion carousels add to the cart themselves - their own fetch,
   * their own section re-render, their own drawer open - so there is no form to
   * submit and no cart:change to watch. The click is the same commitment.
   */
  document.addEventListener('click', function (event) {
    if (!isMobile()) return;

    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('[data-motion-carousel-add]');
    if (!button || button.disabled) return;

    var card = button.closest(CARD_SELECTOR);
    if (!card) return;

    flyToCart(productImage(card));

    if (isListingPage()) holdDrawerShut(DRAWER_HOLD_MAX_MS);
  });

  /*
   * The add has landed. Because the openers all run from here or a frame or two
   * later, this is where the long hold gets cut back to a short tail.
   *
   * The theme opens the drawer from its own cart:change listener, which sits on
   * document in the bubble phase, so this has to capture to get in first. It
   * cancels nothing: the header count, the drawer's own re-render and anything
   * else listening for the event all still see it.
   */
  document.addEventListener(
    'cart:change',
    function (event) {
      if (!isMobile()) return;

      var detail = event.detail || {};
      if (detail.baseEvent !== 'variant:add') return;

      // A collection page is nothing but the grid, so every add there is one of
      // ours whether or not it came through a card we recognise. The home page
      // is not: it also carries app blocks and sections that add to the cart on
      // their own, and those keep opening the drawer as they do today.
      if (pageType() === 'collection' || holding) holdDrawerShut(DRAWER_HOLD_SETTLE_MS);
    },
    true
  );

  /*
   * Adds that bypass the theme (the motion carousels) announce themselves by
   * re-rendering the sections they changed, and open the drawer a frame later.
   * Nothing is armed here - this only brings a hold that is already running
   * down to the tail, so it ends shortly after the add rather than timing out.
   */
  document.addEventListener('shopify:section:load', function () {
    if (holding) holdDrawerShut(DRAWER_HOLD_SETTLE_MS);
  });
})();
